import React from "react";

import i18n from "../../constants/i18n";
import Et3Button from "../../core/et3-button";
import ET3Div from "../../core/et3-div";
import { withLiteObserverAndTheme } from "../../core/et3-enhanced-render";
import PopUpCloseButton from "../../core/et3-shared-components/action-buttons/popUpCloseButton";
import Et3Text from "../../core/et3-text";
import getReactionHistoryStore from "../../stores/user-store/reaction-history-store";
import { Gender, TMessageReactionEvent } from "../../Types";
import {
  HISTORY_CARD_SHADOW,
  HISTORY_LIST_MAX_HEIGHT,
  HISTORY_PANEL_HEADER_HEIGHT,
  HISTORY_POPUP_RADIUS,
  HISTORY_POPUP_WIDTH,
  HISTORY_POPUP_WIDTH_SPLIT,
  HISTORY_ROW_HEIGHT,
  HISTORY_SCRIM,
  HISTORY_SCRIM_BLUR,
  HISTORY_TIMESTAMP_WIDTH,
  HISTORY_TIMESTAMP_WIDTH_SPLIT,
  HISTORY_TRANSITION_MS,
  HISTORY_Z_INDEX,
  REACTION_ACTION_KEYS,
  REACTION_EMOJI_FONT_SIZE
} from "./reaction-constants";

const HistoryRow = withLiteObserverAndTheme<{
  event: TMessageReactionEvent;
  isSplit: boolean;
}>((props) => {
  const { theme, event, isSplit } = props;
  const { colors, unitX } = theme;
  const gender = getReactionHistoryStore().getReactionGender(event.senderId);
  const rowColor =
    gender === Gender.Female
      ? colors.all.translucentPink
      : colors.all.bluishGreenish;
  const timestampWidth = isSplit
    ? HISTORY_TIMESTAMP_WIDTH_SPLIT
    : HISTORY_TIMESTAMP_WIDTH;

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={0}
      height={HISTORY_ROW_HEIGHT}
      fDirection="row"
      alignItems="center"
      justifyContent="flex-start"
      color={rowColor}
      style={{
        width: "100%",
        boxSizing: "border-box",
        paddingLeft: 16 * unitX,
        paddingRight: 16 * unitX,
        gap: 12 * unitX
      }}
      onClick={() => {}}
    >
      <Et3Text
        fontFamily="MEDIUM"
        fontSize={REACTION_EMOJI_FONT_SIZE}
        text={event.reaction}
        isSpan={true}
        color={colors.all.black}
        style={{ width: 44 * unitX }}
      />
      <Et3Text
        fontFamily="BOLD"
        fontSize={16}
        text={i18n.get(REACTION_ACTION_KEYS[event.action])}
        isSpan={true}
        color={colors.all.black}
        style={{ flex: 1 }}
      />
      {event.isCurrent ? (
        <ET3Div
          isScrollable={false}
          isExtendable={false}
          withShadow={false}
          width={90}
          height={24}
          fDirection="row"
          alignItems="center"
          justifyContent="center"
          color={colors.all.bondiBlue}
          style={{
            boxSizing: "border-box",
            borderRadius: 12 * unitX,
            flexShrink: 0
          }}
          onClick={() => {}}
        >
          <Et3Text
            fontFamily="MEDIUM"
            fontSize={12}
            text={i18n.get("REACTION_CURRENT")}
            isSpan={true}
            color={colors.all.white}
            style={{}}
          />
        </ET3Div>
      ) : null}
      <Et3Text
        fontFamily="MEDIUM"
        fontSize={16}
        text={event.happenedOn}
        isSpan={true}
        color={colors.all.black}
        style={{ width: timestampWidth * unitX, textAlign: "right" }}
      />
    </ET3Div>
  );
});

const HistoryPanel = withLiteObserverAndTheme<{
  title: string;
  events: TMessageReactionEvent[];
  isSplit: boolean;
}>((props) => {
  const { theme, title, events, isSplit } = props;
  const { colors, unitX, unitY } = theme;

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={0}
      height={0}
      fDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      color={colors.all.white}
      style={{ width: isSplit ? "50%" : "100%", minWidth: 0 }}
      onClick={() => {}}
    >
      <ET3Div
        isScrollable={false}
        isExtendable={false}
        withShadow={false}
        width={0}
        height={HISTORY_PANEL_HEADER_HEIGHT}
        fDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        color={colors.all.paleBluishGray}
        style={{
          width: "100%",
          boxSizing: "border-box",
          paddingLeft: 16 * unitX,
          paddingRight: 16 * unitX
        }}
        onClick={() => {}}
      >
        <Et3Text
          fontFamily="BOLD"
          fontSize={14}
          text={title}
          isSpan={true}
          color={colors.all.maastrichtBlue}
          style={{}}
        />
      </ET3Div>
      <ET3Div
        isScrollable={true}
        isExtendable={true}
        withShadow={false}
        width={0}
        height={HISTORY_ROW_HEIGHT}
        fDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        color={colors.all.white}
        style={{
          width: "100%",
          maxHeight: HISTORY_LIST_MAX_HEIGHT * unitY
        }}
        onClick={() => {}}
      >
        {events.map((event, index) => (
          <React.Fragment
            key={`${event.senderId}-${event.action}-${event.happenedOn}-${index}`}
          >
            <HistoryRow event={event} isSplit={isSplit} />
          </React.Fragment>
        ))}
      </ET3Div>
    </ET3Div>
  );
});

const ReactionHistoryPopUp = withLiteObserverAndTheme((props) => {
  const { theme } = props;
  const { colors, unitX, unitY } = theme;
  const isVisible = getReactionHistoryStore().isVisible.get();
  const isLoading = getReactionHistoryStore().isLoading.get();
  const events = getReactionHistoryStore().events.get();
  const panels = getReactionHistoryStore().getEventPanels();
  const isSplit = panels.length > 1;
  // isMounted keeps the card in the tree while it animates out, isShown drives
  // the transition itself.
  const [isMounted, setIsMounted] = React.useState(false);
  const [isShown, setIsShown] = React.useState(false);

  React.useEffect(() => {
    if (isVisible) {
      setIsMounted(true);
      const frame = requestAnimationFrame(() => setIsShown(true));
      return () => cancelAnimationFrame(frame);
    }
    setIsShown(false);
    const timer = setTimeout(() => setIsMounted(false), HISTORY_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isVisible]);

  React.useEffect(() => {
    if (!isVisible) {
      return;
    }
    const onKeyDown = (nativeEvent: KeyboardEvent) => {
      if (nativeEvent.key === "Escape") {
        getReactionHistoryStore().close();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isVisible]);

  if (!isMounted) {
    return null;
  }

  const hasEvents = !isLoading && events.length > 0;

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={0}
      height={0}
      fDirection="column"
      alignItems="center"
      justifyContent="center"
      color={HISTORY_SCRIM}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        minWidth: 0,
        height: "100vh",
        minHeight: 0,
        backdropFilter: HISTORY_SCRIM_BLUR,
        zIndex: HISTORY_Z_INDEX,
        opacity: isShown ? 1 : 0,
        transition: `opacity ${HISTORY_TRANSITION_MS}ms ease`
      }}
      onClick={() => getReactionHistoryStore().close()}
    >
      <div
        onClick={(clickEvent) => clickEvent.stopPropagation()}
        style={{
          opacity: isShown ? 1 : 0,
          transform: isShown
            ? "translateY(0) scale(1)"
            : `translateY(${12 * unitY}px) scale(0.96)`,
          transition: `opacity ${HISTORY_TRANSITION_MS}ms ease, transform ${HISTORY_TRANSITION_MS}ms ease`
        }}
      >
        <ET3Div
          isScrollable={false}
          isExtendable={true}
          withShadow={false}
          width={isSplit ? HISTORY_POPUP_WIDTH_SPLIT : HISTORY_POPUP_WIDTH}
          height={0}
          fDirection="column"
          alignItems="center"
          justifyContent="flex-start"
          color={colors.all.white}
          style={{
            borderRadius: HISTORY_POPUP_RADIUS * unitX,
            boxShadow: HISTORY_CARD_SHADOW,
            overflow: "hidden"
          }}
          onClick={() => {}}
        >
          <ET3Div
            isScrollable={false}
            isExtendable={false}
            withShadow={false}
            width={0}
            height={80}
            fDirection="row"
            alignItems="center"
            justifyContent="space-between"
            color={colors.all.white}
            style={{
              width: "100%",
              boxSizing: "border-box",
              paddingLeft: 32 * unitX,
              paddingRight: 32 * unitX
            }}
            onClick={() => {}}
          >
            <Et3Text
              fontFamily="BOLD"
              fontSize={24}
              text={i18n.get("REACTION_HISTORIES")}
              isSpan={true}
              color={colors.all.black}
              style={{}}
            />
            <PopUpCloseButton
              onClick={() => getReactionHistoryStore().close()}
            />
          </ET3Div>

          {hasEvents ? (
            <ET3Div
              isScrollable={false}
              isExtendable={true}
              withShadow={false}
              width={0}
              height={0}
              fDirection="row"
              alignItems="flex-start"
              justifyContent="flex-start"
              color={colors.all.white}
              style={{ width: "100%" }}
              onClick={() => {}}
            >
              {panels.map((panel, index) => (
                <React.Fragment key={panel.senderId}>
                  {index > 0 ? (
                    <ET3Div
                      isScrollable={false}
                      isExtendable={true}
                      withShadow={false}
                      width={1}
                      height={0}
                      fDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      color={colors.all.paleBluishGray}
                      style={{ alignSelf: "stretch", flexShrink: 0 }}
                      onClick={() => {}}
                    />
                  ) : null}
                  <HistoryPanel
                    title={i18n.getWithExtraData(
                      panel.isMessageSender
                        ? "REACTION_SENDER_WITH_ID"
                        : "REACTION_RECIPIENT_WITH_ID",
                      `${panel.senderId}`
                    )}
                    events={panel.events}
                    isSplit={isSplit}
                  />
                </React.Fragment>
              ))}
            </ET3Div>
          ) : (
            <ET3Div
              isScrollable={false}
              isExtendable={false}
              withShadow={false}
              width={0}
              height={HISTORY_ROW_HEIGHT}
              fDirection="row"
              alignItems="center"
              justifyContent="center"
              color={colors.all.white}
              style={{ width: "100%" }}
              onClick={() => {}}
            >
              <Et3Text
                fontFamily="LIGHT"
                fontSize={16}
                text={i18n.get(isLoading ? "LOADING" : "NO_REACTION_HISTORY")}
                isSpan={true}
                color={colors.all.dimGray}
                style={{}}
              />
            </ET3Div>
          )}

          <ET3Div
            isScrollable={false}
            isExtendable={false}
            withShadow={false}
            width={0}
            height={80}
            fDirection="row"
            alignItems="center"
            justifyContent="center"
            color={colors.all.white}
            style={{ width: "100%" }}
            onClick={() => {}}
          >
            <Et3Button
              text={"OK"}
              isEnabled={true}
              size={"LARGE"}
              type={"GET"}
              onClick={() => getReactionHistoryStore().close()}
            />
          </ET3Div>
        </ET3Div>
      </div>
    </ET3Div>
  );
});
export default ReactionHistoryPopUp;
